import type { Party } from "@/mocks/partes";
import PartyStatusBadge from "./PartyStatusBadge";
import PartyTypeBadge from "./PartyTypeBadge";
import PartyRoleBadge from "./PartyRoleBadge";

interface PartyDetailDrawerProps {
  party: Party;
  onClose: () => void;
}

export default function PartyDetailDrawer({ party, onClose }: PartyDetailDrawerProps) {
  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-xl flex flex-col animate-slide-in-right">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <h3 className="text-lg font-semibold text-foreground-900 font-heading">Detalhe da Parte</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-background-50 transition-colors">
            <i className="ri-close-line w-full h-full flex items-center justify-center text-xl" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h4 className="text-base font-bold text-foreground-900">{party.name}</h4>
              <div className="flex items-center gap-2 mt-2">
                <PartyTypeBadge type={party.type} />
                <PartyStatusBadge status={party.status} />
              </div>
            </div>
          </div>

          {/* Document & Registration */}
          <div className="bg-background-50 rounded-xl p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-foreground-400">CPF/CNPJ</span>
              <span className="text-foreground-800 font-medium">{party.documentId}</span>
            </div>
            {party.registrationNumber && (
              <div className="flex justify-between text-sm">
                <span className="text-foreground-400">Registro profissional</span>
                <span className="text-foreground-800 font-medium">{party.registrationNumber}</span>
              </div>
            )}
            {party.council && (
              <div className="flex justify-between text-sm">
                <span className="text-foreground-400">Conselho</span>
                <span className="text-foreground-800 font-medium">{party.council}</span>
              </div>
            )}
            {party.specialty && (
              <div className="flex justify-between text-sm">
                <span className="text-foreground-400">Especialidade</span>
                <span className="text-foreground-800 font-medium">{party.specialty}</span>
              </div>
            )}
          </div>

          {/* Contact */}
          <div className="bg-background-50 rounded-xl p-4 space-y-3">
            <h5 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Contatos</h5>
            <div className="flex justify-between text-sm">
              <span className="text-foreground-400">E-mail</span>
              <span className="text-foreground-800 font-medium">{party.email}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-foreground-400">Telefone</span>
              <span className="text-foreground-800 font-medium">{party.phone}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-foreground-400">Endereço</span>
              <span className="text-foreground-800 font-medium text-right max-w-[240px]">{party.address}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-foreground-400">Município/UF</span>
              <span className="text-foreground-800 font-medium">{party.municipality}/{party.uf}</span>
            </div>
          </div>

          {/* Role & Institution */}
          <div className="bg-background-50 rounded-xl p-4 space-y-3">
            <h5 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Papel e Instituição</h5>
            <div className="flex justify-between text-sm items-center">
              <span className="text-foreground-400">Papel principal</span>
              <PartyRoleBadge role={party.primaryRole} />
            </div>
            {party.institution !== "—" && (
              <div className="flex justify-between text-sm">
                <span className="text-foreground-400">Instituição</span>
                <span className="text-foreground-800 font-medium">{party.institution}</span>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-background-50 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-primary-600 font-heading">{party.activeLinks}</p>
              <p className="text-xs text-foreground-400">Vínculos ativos</p>
            </div>
            <div className="bg-background-50 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-accent-600 font-heading">{party.hasDocuments ? "Sim" : "Não"}</p>
              <p className="text-xs text-foreground-400">Documentos</p>
            </div>
            <div className="bg-background-50 rounded-xl p-3 text-center">
              <p className="text-xl font-bold text-secondary-600 font-heading">{party.status}</p>
              <p className="text-xs text-foreground-400">Status</p>
            </div>
          </div>

          {/* Dates */}
          <div className="bg-background-50 rounded-xl p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-foreground-400">Cadastro</span>
              <span className="text-foreground-800 font-medium">{party.registrationDate}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-foreground-400">Última atualização</span>
              <span className="text-foreground-800 font-medium">{party.lastUpdate}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="border-t border-background-200 px-6 py-4 flex gap-2">
          <button className="flex-1 px-4 py-2 text-sm font-semibold text-primary-700 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors whitespace-nowrap">
            <i className="ri-edit-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Editar
          </button>
          <button className="flex-1 px-4 py-2 text-sm font-semibold text-accent-700 bg-accent-50 rounded-lg hover:bg-accent-100 transition-colors whitespace-nowrap">
            <i className="ri-link-m w-4 h-4 inline-flex items-center justify-center mr-1" />
            Criar vínculo
          </button>
          <button className="flex-1 px-4 py-2 text-sm font-semibold text-secondary-700 bg-secondary-50 rounded-lg hover:bg-secondary-100 transition-colors whitespace-nowrap">
            <i className="ri-file-list-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Documentos
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}